import { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  createInvestmentRequest,
  clearCreateInvestmentSuccess,
  getPlansRequest,
} from "../../../redux/action";
import { PrimaryButton } from "../../../components/Buttons";
import { AuthNav, SideNav } from "../../../components/Navbar/Webpages/AuthNav";
import { FaArrowUp, FaCheckCircle, FaLock } from "react-icons/fa";
import CurrencyDisplay from "../../../components/Currency/CurrencyDisplay";
import { useCurrency } from "../../../hooks/useCurrency";

// Color mapping for different plans
const PLAN_COLORS = [
  "bg-primary-500",
  "bg-indigo-500",
  "bg-purple-500",
  "bg-emerald-500",
  "bg-orange-500",
  "bg-pink-500",
];

const Proceed = ({ handleClose }) => {
  const dispatch = useDispatch();
  const {
    getCurrencySymbol,
    convertUSDToDisplay,
    convertDisplayToUSD,
    isLoaded,
  } = useCurrency();
  const [amount, setAmount] = useState("");
  const [activeTier, setActiveTier] = useState(null);
  const [error, setError] = useState("");
  const [usdAmount, setUsdAmount] = useState(0); // Store USD equivalent for API

  const {
    createInvestmentSuccess,
    createInvestmentError,
    createInvestmentLoading,
    user,
    plans,
    getPlansLoading,
  } = useSelector((state) => ({
    createInvestmentSuccess: state.ajaxStatuses.success.createInvestment,
    createInvestmentError: state.ajaxStatuses.errors.createInvestment,
    createInvestmentLoading: state.loadingIndicator.createInvestmentLoading,
    user: state.userData.user,
    plans: state.planData.plans || [],
    getPlansLoading: state.loadingIndicator.getPlansLoading,
  }));

  // Transform plans data to match the TIERS structure
  const TIERS = useMemo(() => {
    return plans.map((plan, index) => ({
      name: plan.planName,
      min: plan.minAmount,
      max: plan.maxAmount,
      rate: plan.profitProduction, // Daily rate
      color: PLAN_COLORS[index % PLAN_COLORS.length],
      duration: plan.duration,
      _id: plan._id,
    }));
  }, [plans]);

  // Fetch plans on component mount
  useEffect(() => {
    dispatch(getPlansRequest());
  }, [dispatch]);

  // --- Logic ---

  // Calculate tier and projected returns whenever amount changes
  const calculations = useMemo(() => {
    if (!isLoaded || !amount || TIERS.length === 0) {
      return { tier: null, profit: 0, total: 0, error: "" };
    }

    const numAmount = Number(amount);
    const currencySymbol = getCurrencySymbol();

    // Convert user input from display currency to USD
    const usdInputAmount = convertDisplayToUSD(numAmount);
    setUsdAmount(usdInputAmount);

    // Convert USD tier limits to display currency for comparison
    const tierComparisons = TIERS.map((tier) => ({
      ...tier,
      minDisplay: convertUSDToDisplay(tier.min),
      maxDisplay: convertUSDToDisplay(tier.max),
    }));

    // Convert user balance from USD to display currency for comparison
    const balanceInDisplayCurrency = convertUSDToDisplay(user.balance);

    // Find active tier using display currency amounts
    const tier = tierComparisons.find(
      (t) => numAmount >= t.minDisplay && numAmount <= t.maxDisplay,
    );

    // Check minimum investment
    if (numAmount > 0 && !tier && numAmount < tierComparisons[0].minDisplay) {
      return {
        error: `Minimum investment is ${currencySymbol}${tierComparisons[0].minDisplay.toFixed(
          2,
        )}`,
        profit: 0,
        total: 0,
        tier: null,
      };
    }

    // Check sufficient balance (compare in display currency)
    if (numAmount > balanceInDisplayCurrency) {
      return {
        error: "Insufficient balance",
        profit: 0,
        total: 0,
        tier: null,
      };
    }

    // Calculate profit and total using USD amounts (for accuracy)
    if (tier) {
      const profit = usdInputAmount * (tier.rate / 100);
      const total = usdInputAmount + profit;

      return {
        tier,
        profit,
        total,
        error: "",
      };
    }

    return { tier: null, profit: 0, total: 0, error: "" };
  }, [
    amount,
    user.balance,
    getCurrencySymbol,
    convertUSDToDisplay,
    convertDisplayToUSD,
    isLoaded,
    TIERS,
  ]);

  // Update local state based on calculations
  useEffect(() => {
    setActiveTier(calculations.tier);
    setError(calculations.error);
  }, [calculations]);

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {
      if (!amount || error || !activeTier) return;

      const payload = {
        user: user._id,
        amount: usdAmount, // Use USD amount for API
        expectedReturn: calculations.total,
        profitProduction: activeTier.rate,
        planName: activeTier.name,
      };

      dispatch(createInvestmentRequest(payload));
    },
  });

  // Handle Redux Side Effects
  useEffect(() => {
    if (createInvestmentError) toast.error(createInvestmentError);
  }, [createInvestmentError]);

  useEffect(() => {
    if (createInvestmentSuccess) {
      toast.success("Investment started successfully!");
      if (handleClose) handleClose();
      dispatch(clearCreateInvestmentSuccess());
      setAmount("");
    }
  }, [createInvestmentSuccess, dispatch, handleClose]);

  const handleMax = () => {
    const maxDisplayAmount = convertUSDToDisplay(user.balance);
    setAmount(maxDisplayAmount.toString());
  };

  return (
    <div className="bg-gray-50/50 min-h-screen pb-10">
      <AuthNav showLogo={false} title="New Investment" />
      <SideNav />

      <div className="container mx-auto max-w-5xl px-4  pt-20">
        {/* LEFT COLUMN: Input & Calculator */}
        <div className="lg:col-span-7 space-y-6">
          {/* Balance Header */}
          <div className="text-center mb-8">
            <p className="text-sm text-gray-500 font-medium mb-1">
              Available to Invest
            </p>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-gray-900 tracking-tight">
              <CurrencyDisplay usdAmount={user.balance} className="inline" />
            </h1>
          </div>

          {/* Main Calculator */}
          <div className="bg-white rounded-2xl shadow-sm border  p-6 md:p-8">
            <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-4 text-center">
              Enter Amount
            </label>

            {/* Big Input */}
            <div className="relative max-w-xs mx-auto mb-8">
              <div className="absolute top-1/2 -translate-y-1/2 left-0 text-3xl font-light text-gray-300">
                {getCurrencySymbol()}
              </div>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="w-full text-center text-5xl font-bold text-gray-800 placeholder-gray-200 border-b-2  focus:border-primary-500 focus:outline-none py-2 bg-transparent transition-colors"
              />
              <button
                onClick={handleMax}
                className="absolute -right-8 top-1/2 -translate-y-1/2 text-xs font-bold text-primary-600 bg-primary-50 hover:bg-primary-100 px-2 py-1 rounded transition-colors"
              >
                MAX
              </button>
            </div>

            {/* Error Message */}
            {error && (
              <div className="text-center bg-red-50 text-red-600 text-sm py-2 px-4 rounded-lg mb-6 animate-pulse">
                {error}
              </div>
            )}

            {/* Loading indicator for rate fetching */}
            {!isLoaded && (
              <div className="text-center bg-blue-50 text-blue-600 text-sm py-2 px-4 rounded-lg mb-6">
                Loading exchange rates...
              </div>
            )}

            {/* Loading indicator for plans */}
            {getPlansLoading && (
              <div className="text-center bg-blue-50 text-blue-600 text-sm py-2 px-4 rounded-lg mb-6">
                Loading investment plans...
              </div>
            )}

            {/* Summary Projection (Only shows if valid) */}
            {activeTier && (
              <div className="bg-gray-50 rounded-xl p-5 border  space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <span className="text-gray-500 text-sm">Selected Plan</span>
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-bold text-white uppercase tracking-wide ${activeTier.color}`}
                  >
                    {activeTier.name} Plan
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      Daily Profit ({activeTier.rate}%)
                    </p>
                    <p className="text-lg font-bold text-green-600 flex items-center gap-1">
                      +${calculations.profit.toFixed(2)}{" "}
                      <FaArrowUp className="text-xs" />
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 mb-1">Total Return</p>
                    <p className="text-lg font-bold text-gray-900">
                      <CurrencyDisplay
                        usdAmount={calculations.total * 5}
                        className="inline"
                      />
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="mt-8">
              <PrimaryButton
                action={formik.handleSubmit}
                loading={createInvestmentLoading}
                text={
                  createInvestmentLoading
                    ? "Processing..."
                    : "Confirm Investment"
                }
                disabled={
                  !amount ||
                  !!error ||
                  !activeTier ||
                  !isLoaded ||
                  getPlansLoading
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Proceed;
